import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import apiClient, { adminAPI } from '@/utils/api';
import { toast } from 'sonner';
import { Sparkles, Plus, Trash2, Clock, TrendingDown } from 'lucide-react';

const FlashSaleManager = () => {
  const navigate = useNavigate();
  const [flashSales, setFlashSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_time: '',
    end_time: '',
    category_filter: 'all',
    max_discount: 70,
    min_stock: 1,
    member_only: false
  });

  useEffect(() => {
    fetchFlashSales();
  }, []);

  const fetchFlashSales = async () => {
    try {
      const response = await apiClient.get('/admin/flash-sales');
      setFlashSales(response.data);
    } catch (error) {
      toast.error('Failed to fetch flash sales');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFlashSale = async (e) => {
    e.preventDefault();
    
    try {
      await apiClient.post('/admin/flash-sales/create', formData);
      toast.success('Flash sale created successfully!');
      setShowCreateForm(false);
      setFormData({
        name: '',
        description: '',
        start_time: '',
        end_time: '',
        category_filter: 'all',
        max_discount: 70,
        min_stock: 1,
        member_only: false
      });
      fetchFlashSales();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create flash sale');
    }
  };

  const handleDeleteFlashSale = async (saleId) => {
    if (!confirm('Are you sure you want to cancel this flash sale?')) return;
    
    try {
      await apiClient.delete(`/admin/flash-sales/${saleId}`);
      toast.success('Flash sale cancelled');
      fetchFlashSales();
    } catch (error) {
      toast.error('Failed to cancel flash sale');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-[#1A3A52] bg-[#1A3A52]/10 border-[#1A3A52]/20';
      case 'scheduled':
        return 'text-[#5A6C7D] bg-[#5A6C7D]/10 border-[#5A6C7D]/20';
      case 'ended':
        return 'text-[#8B9AAC] bg-[#8B9AAC]/10 border-[#8B9AAC]/20';
      case 'cancelled':
        return 'text-[#2C3E50] bg-[#2C3E50]/10 border-[#2C3E50]/20';
      default:
        return 'text-[#8B9AAC] bg-[#8B9AAC]/10 border-[#8B9AAC]/20';
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Navbar */}
      <nav className="border-b border-[#E5E1DC] bg-white/90 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/admin')}>
              <Sparkles className="h-7 w-7 text-[#1A3A52]" strokeWidth={1.5} />
              <span className="text-2xl font-serif font-semibold text-[#2C3E50]">
                LuxLyx Admin
              </span>
            </div>
            <Button
              data-testid="create-flash-sale-btn"
              onClick={() => setShowCreateForm(true)}
              className="bg-[#1A3A52] text-white hover:bg-[#2D4F6B] font-medium"
            >
              <Plus className="h-5 w-5 mr-2" strokeWidth={1.5} />
              Create Flash Sale
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12">
        <h1 className="text-4xl font-serif font-semibold mb-8 flex items-center text-[#2C3E50]">
          <TrendingDown className="h-10 w-10 mr-3 text-[#1A3A52]" strokeWidth={1.5} />
          Flash Sale Manager
        </h1>

        {/* Create Form Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
            <div className="bg-white border border-[#E5E1DC] rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              <h2 className="text-2xl font-serif font-semibold mb-6 text-[#2C3E50]">Create New Flash Sale</h2>
              
              <form onSubmit={handleCreateFlashSale} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-[#2C3E50] font-medium">Sale Name</Label>
                  <Input
                    id="name"
                    data-testid="name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g. Weekend Electronics Blowout"
                    className="mt-2 bg-[#FAF8F5] border-[#E5E1DC] text-[#2C3E50] focus:border-[#1A3A52]"
                  />
                </div>

                <div>
                  <Label htmlFor="description" className="text-[#2C3E50] font-medium">Description</Label>
                  <Textarea
                    id="description"
                    data-testid="description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    placeholder="Describe this flash sale event..."
                    className="mt-2 bg-[#FAF8F5] border-[#E5E1DC] text-[#2C3E50] focus:border-[#1A3A52]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start_time" className="text-[#2C3E50] font-medium">Start Time</Label>
                    <Input
                      id="start_time"
                      data-testid="start-time-input"
                      type="datetime-local"
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value + ':00Z' })}
                      required
                      className="mt-2 bg-[#FAF8F5] border-[#E5E1DC] text-[#2C3E50] focus:border-[#1A3A52]"
                    />
                  </div>

                  <div>
                    <Label htmlFor="end_time" className="text-[#2C3E50] font-medium">End Time</Label>
                    <Input
                      id="end_time"
                      data-testid="end-time-input"
                      type="datetime-local"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value + ':00Z' })}
                      required
                      className="mt-2 bg-[#FAF8F5] border-[#E5E1DC] text-[#2C3E50] focus:border-[#1A3A52]"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="category_filter" className="text-[#2C3E50] font-medium">Category Filter (Optional)</Label>
                  <Select 
                    value={formData.category_filter} 
                    onValueChange={(value) => setFormData({ ...formData, category_filter: value })}
                  >
                    <SelectTrigger data-testid="category-select" className="mt-2 bg-[#FAF8F5] border-[#E5E1DC] text-[#2C3E50]">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      <SelectItem value="Electronics">Electronics</SelectItem>
                      <SelectItem value="Fashion">Fashion</SelectItem>
                      <SelectItem value="Home">Home</SelectItem>
                      <SelectItem value="Sports">Sports</SelectItem>
                      <SelectItem value="Beauty">Beauty</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="max_discount" className="text-[#2C3E50] font-medium">Max Discount (%)</Label>
                    <Input
                      id="max_discount"
                      data-testid="max-discount-input"
                      type="number"
                      value={formData.max_discount}
                      onChange={(e) => setFormData({ ...formData, max_discount: parseFloat(e.target.value) })}
                      min="0"
                      max="100"
                      className="mt-2 bg-[#FAF8F5] border-[#E5E1DC] text-[#2C3E50] focus:border-[#1A3A52]"
                    />
                  </div>

                  <div>
                    <Label htmlFor="min_stock" className="text-[#2C3E50] font-medium">Min Stock Required</Label>
                    <Input
                      id="min_stock"
                      data-testid="min-stock-input"
                      type="number"
                      value={formData.min_stock}
                      onChange={(e) => setFormData({ ...formData, min_stock: parseInt(e.target.value) })}
                      min="0"
                      className="mt-2 bg-[#FAF8F5] border-[#E5E1DC] text-[#2C3E50] focus:border-[#1A3A52]"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <input
                    id="member_only"
                    data-testid="member-only-checkbox"
                    type="checkbox"
                    checked={formData.member_only}
                    onChange={(e) => setFormData({ ...formData, member_only: e.target.checked })}
                    className="w-5 h-5 border-[#E5E1DC] rounded"
                  />
                  <Label htmlFor="member_only" className="text-[#5A6C7D] cursor-pointer">
                    Premium Members Only
                  </Label>
                </div>

                <div className="flex space-x-4">
                  <Button
                    data-testid="cancel-btn"
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    variant="outline"
                    className="flex-1 border-[#E5E1DC] text-[#5A6C7D] hover:bg-[#F5F3F0]"
                  >
                    Cancel
                  </Button>
                  <Button
                    data-testid="create-btn"
                    type="submit"
                    className="flex-1 bg-[#1A3A52] text-white hover:bg-[#2D4F6B] font-medium"
                  >
                    Create Flash Sale
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Flash Sales List */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A3A52]"></div>
          </div>
        ) : flashSales.length === 0 ? (
          <div className="text-center py-20 bg-white border border-[#E5E1DC] rounded-2xl">
            <Clock className="h-16 w-16 text-[#8B9AAC] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-[#5A6C7D] text-xl mb-6">No flash sales yet</p>
            <Button
              data-testid="create-first-sale-btn"
              onClick={() => setShowCreateForm(true)}
              className="bg-[#1A3A52] text-white hover:bg-[#2D4F6B] font-medium"
            >
              Create Your First Flash Sale
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {flashSales.map((sale) => (
              <div
                key={sale.id}
                data-testid={`flash-sale-${sale.id}`}
                className="bg-white border border-[#E5E1DC] rounded-2xl p-6 shadow-sm"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-2xl font-serif font-semibold text-[#2C3E50]">{sale.name}</h3>
                      <div className={`px-3 py-1 rounded-full border text-sm font-medium ${getStatusColor(sale.status)}`}>
                        {sale.status.toUpperCase()}
                      </div>
                    </div>
                    <p className="text-[#5A6C7D] mb-3">{sale.description}</p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-[#8B9AAC]">Start: </span>
                        <span className="text-[#2C3E50]">
                          {new Date(sale.start_time).toLocaleString()}
                        </span>
                      </div>
                      <div>
                        <span className="text-[#8B9AAC]">End: </span>
                        <span className="text-[#2C3E50]">
                          {new Date(sale.end_time).toLocaleString()}
                        </span>
                      </div>
                      <div>
                        <span className="text-[#8B9AAC]">Category: </span>
                        <span className="text-[#2C3E50]">
                          {sale.category_filter || 'All'}
                        </span>
                      </div>
                      <div>
                        <span className="text-[#8B9AAC]">Max Discount: </span>
                        <span className="text-[#1A3A52] font-semibold">
                          {sale.rules?.max_discount}%
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-2 ml-4">
                    <Button
                      data-testid={`delete-sale-${sale.id}`}
                      onClick={() => handleDeleteFlashSale(sale.id)}
                      variant="outline"
                      size="sm"
                      className="border-[#E5E1DC] text-[#5A6C7D] hover:bg-[#F5F3F0]"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>

                {/* Stats */}
                {sale.stats && (
                  <div className="grid grid-cols-4 gap-4 pt-4 border-t border-[#E5E1DC]">
                    <div>
                      <div className="text-2xl font-serif font-semibold text-[#1A3A52]">{sale.stats.total_views || 0}</div>
                      <div className="text-sm text-[#8B9AAC] uppercase tracking-wide">Views</div>
                    </div>
                    <div>
                      <div className="text-2xl font-serif font-semibold text-[#1A3A52]">{sale.stats.total_orders || 0}</div>
                      <div className="text-sm text-[#8B9AAC] uppercase tracking-wide">Orders</div>
                    </div>
                    <div>
                      <div className="text-2xl font-serif font-semibold text-[#1A3A52]">${(sale.stats.total_revenue || 0).toFixed(2)}</div>
                      <div className="text-sm text-[#8B9AAC] uppercase tracking-wide">Revenue</div>
                    </div>
                    <div>
                      <div className="text-2xl font-serif font-semibold text-[#1A3A52]">{sale.stats.products_sold || 0}</div>
                      <div className="text-sm text-[#8B9AAC] uppercase tracking-wide">Sold</div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlashSaleManager;
